import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Chip
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import GetAppIcon from '@material-ui/icons/GetApp';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import TopMenu from '../TopMenu';
import firebase from 'firebase/app';
import 'firebase/database';
import 'firebase/storage';
import AIEvaluationService from '../../services/AIEvaluationService';
import PdfTextExtractionService from '../../services/PdfTextExtractionService';
import VideoAnalysisService from '../../services/VideoAnalysisService';
import { getApiBaseUrl } from '../../services/ApiUtils';
import swal from 'sweetalert';

const Playground = () => {
  const [examCode, setExamCode] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [customEvaluation, setCustomEvaluation] = useState(null);
  const [evaluating, setEvaluating] = useState(false);

  const fetchResults = async () => {
    if (!examCode.trim() || !candidateEmail.trim()) {
      swal('Error', 'Please enter both exam code and candidate email', 'error');
      return;
    }

    setLoading(true);
    try {
      // Sanitize email for Firebase path
      const sanitizedEmail = candidateEmail.replace(/\./g, ',');

      // Fetch exam data
      const examRef = firebase.database().ref(`TestPapers/${examCode}`);
      const examSnapshot = await examRef.once('value');
      
      if (!examSnapshot.exists()) {
        swal('Error', 'Exam not found', 'error');
        setLoading(false);
        return;
      }

      const examData = examSnapshot.val();
      
      // Normalize exam structure
      const normalizedExam = {
        title: examData.title || examData.examTitle,
        sections: examData.sections ? examData.sections.map(section => ({
          id: section.id,
          title: section.t || section.title,
          questions: (section.q || section.questions || []).map(q => ({
            id: q.id,
            type: q.type,
            text: q.text || q.question,
            maxScore: q.maxScore !== undefined ? Number(q.maxScore) : (q.ms !== undefined ? Number(q.ms) : 10),
            evaluationPoints: (() => {
              const evalPoints = q.evaluationPoints || '';
              const evalKeywords = q.ek || q.evaluationKeywords || '';
              if (evalPoints && evalKeywords) {
                const pointsArray = typeof evalPoints === 'string' 
                  ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
                  : Array.isArray(evalPoints) ? evalPoints : [];
                const keywordsArray = typeof evalKeywords === 'string'
                  ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
                  : Array.isArray(evalKeywords) ? evalKeywords : [];
                const combined = [...pointsArray, ...keywordsArray];
                return [...new Set(combined)];
              }
              return evalPoints ? (typeof evalPoints === 'string' ? evalPoints.split(',').map(p => p.trim()).filter(Boolean) : evalPoints) : 
                     (evalKeywords ? (typeof evalKeywords === 'string' ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean) : evalKeywords) : []);
            })(),
            enableAIEvaluation: q.ai !== undefined ? q.ai : (q.enableAIEvaluation || false)
          }))
        })) : []
      };

      // Fetch submission results
      const submissionRef = firebase.database().ref(`Exam_results/${examCode}/${sanitizedEmail}`);
      const submissionSnapshot = await submissionRef.once('value');
      
      if (!submissionSnapshot.exists()) {
        swal('Error', 'No submission found for this candidate', 'error');
        setLoading(false);
        return;
      }

      const submissionData = submissionSnapshot.val();
      const answers = submissionData.answers || {};
      const aiEvaluation = submissionData.aiEvaluation || {};
      const evaluations = aiEvaluation.evaluations || {};

      // Process questions with their answers and evaluations
      const processedQuestions = [];
      
      normalizedExam.sections.forEach((section, sectionIndex) => {
        section.questions.forEach((question, questionIndex) => {
          const answer = answers[question.id];
          const evaluation = evaluations[question.id];
          
          if (answer || evaluation) {
            processedQuestions.push({
              ...question,
              sectionTitle: section.title,
              sectionIndex,
              questionIndex,
              answer,
              evaluation
            });
          }
        });
      });

      setQuestions(processedQuestions);
      
      if (processedQuestions.length === 0) {
        swal('Info', 'No questions with answers found', 'info');
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      swal('Error', `Failed to fetch results: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const extractTextFromAnswer = async (answer, question, skipExtraction = true) => {
    let extractedText = '';
    let extractionSource = '';

    if (!answer) return { extractedText: '', extractionSource: 'No answer' };

    // First, check for existing stored data in Firebase (no API calls)
    if (answer.transcript) {
      extractedText = answer.transcript;
      extractionSource = 'Transcript (stored)';
    } else if (answer.pdfFile?.extractedText) {
      extractedText = answer.pdfFile.extractedText;
      extractionSource = 'PDF (stored)';
    } else if (answer.text) {
      extractedText = typeof answer.text === 'string' ? answer.text : '';
      extractionSource = 'Text Answer';
    } else if (answer.answer) {
      extractedText = typeof answer.answer === 'string' ? answer.answer : '';
      extractionSource = 'Text Answer';
    }

    // Only extract if skipExtraction is false and we don't have stored data
    if (!extractedText && !skipExtraction) {
      // Check for PDF
      if (answer.pdfFile) {
        try {
          const pdfResult = await PdfTextExtractionService.extractText(answer);
          if (pdfResult && pdfResult.text) {
            extractedText = pdfResult.text;
            extractionSource = 'PDF';
          }
        } catch (error) {
          console.error('PDF extraction error:', error);
        }
      }

      // Check for video
      if (!extractedText && (answer.videoUrl || answer.videoPath)) {
        try {
          const videoUrl = answer.videoUrl || answer.videoPath;
          const transcriptionResult = await VideoAnalysisService.transcribeVideo(videoUrl);
          if (transcriptionResult.success && transcriptionResult.transcript) {
            extractedText = transcriptionResult.transcript;
            extractionSource = 'Video/Audio';
          }
        } catch (error) {
          console.error('Video transcription error:', error);
        }
      }
    }

    return { extractedText, extractionSource };
  };

  const handleQuestionSelect = async (question) => {
    setSelectedQuestion(null);
    setCustomEvaluation(null);
    setCustomPrompt('');

    // Extract text from answer - skip API calls, only use stored data from Firebase
    const { extractedText, extractionSource } = await extractTextFromAnswer(question.answer, question, true);
    
    setSelectedQuestion({
      ...question,
      extractedText,
      extractionSource
    });
  };

  const buildPrompt = (question, extractedText, customPromptText = null) => {
    const evaluationPoints = question.evaluationPoints || [];
    const maxScore = question.maxScore || 10;

    if (customPromptText) {
      // Use custom prompt, but replace placeholders if needed
      return customPromptText
        .replace(/\$\{question\}/g, question.text || '')
        .replace(/\$\{answer\}/g, extractedText || '')
        .replace(/\$\{maxScore\}/g, maxScore.toString());
    }

    // Build default prompt based on question type
    if (evaluationPoints.length > 0) {
      return `You are an expert evaluator. Analyze the following response to determine how well it SATISFIES each evaluation criterion.

IMPORTANT: Do NOT just check if the criterion is mentioned in the text. Instead, evaluate whether the text actually SATISFIES or MEETS the criterion.

QUESTION: ${question.text || question.question}
USER'S RESPONSE: ${extractedText}

EVALUATION CRITERIA (evaluate how well the response SATISFIES each):
${evaluationPoints.map((point, index) => `${index + 1}. ${point}`).join('\n')}

For each evaluation criterion, determine:
1. Does the response SATISFY this criterion? (true/false)
2. How well does it satisfy it? (0-100 score, where 100 = fully satisfies, 0 = does not satisfy)
3. Brief explanation of why it does or doesn't satisfy the criterion

Respond in this exact JSON format:
{
  "evaluations": [
    {
      "criterion": "exact evaluation point text",
      "satisfied": true/false,
      "satisfactionScore": 0-100,
      "reason": "brief explanation of how well it satisfies the criterion"
    }
  ]
}`;
    } else {
      return `You are an expert evaluator. Evaluate this answer for the given question and provide a score that accurately reflects the quality and issues mentioned in your feedback.

Question: ${question.text || question.question}
Answer: ${extractedText}

SCORING GUIDELINES (out of ${maxScore} points):
- 9-10 points (90-100%): Excellent answer - clear, coherent, well-structured, comprehensive, with minimal or no errors
- 7-8 points (70-89%): Good answer - mostly clear and coherent, with minor issues or gaps
- 5-6 points (50-69%): Average answer - addresses the question but has noticeable issues
- 3-4 points (30-49%): Below average - significant issues
- 1-2 points (10-29%): Poor answer - major issues
- 0 points (0-9%): Very poor - fails to address the question or is completely incoherent

Provide evaluation in this JSON format:
{
  "score": number (must align with feedback issues),
  "maxScore": ${maxScore},
  "feedback": string (detailed feedback explaining issues and strengths),
  "confidence": number (0-1),
  "textScore": number (same as score for consistency)
}`;
    }
  };

  const reEvaluateWithCustomPrompt = async () => {
    if (!selectedQuestion || !customPrompt.trim()) {
      swal('Error', 'Please enter a custom prompt', 'error');
      return;
    }

    setEvaluating(true);
    try {
      const extractedText = selectedQuestion.extractedText || '';
      
      // Send custom prompt as-is to backend - backend will replace placeholders
      let prompt = customPrompt;

      // Call evaluation API with custom prompt
      const apiBaseUrl = getApiBaseUrl();
      const question = selectedQuestion;
      const answer = {
        text: extractedText,
        ...selectedQuestion.answer
      };

      let endpoint = '/api/evaluate-text';
      if (question.type === 'code' || question.subType === 'code') {
        endpoint = '/api/evaluate-code';
      } else if (question.type === 'scenario-text') {
        endpoint = '/api/evaluate-scenario-text';
      }

      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question,
          answer,
          maxScore: question.maxScore || 10,
          evaluationPoints: question.evaluationPoints || [],
          customPrompt: prompt // Pass custom prompt
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.details || `API error: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setCustomEvaluation({
          ...result.data,
          promptUsed: result.data.promptUsed || prompt // Use backend's processed prompt, fallback to user prompt
        });
      } else {
        throw new Error(result.error || 'Evaluation failed');
      }
    } catch (error) {
      console.error('Re-evaluation error:', error);
      swal('Error', `Failed to re-evaluate: ${error.message}`, 'error');
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <TopMenu showHome showLogout />
      <Typography variant="h5" gutterBottom style={{ marginTop: 80, marginBottom: 24 }}>
        AI Evaluation Playground
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
        Compare original AI evaluations with custom prompts to refine evaluation criteria
      </Typography>

      <Paper style={{ padding: 24, marginBottom: 24 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Exam Code"
              value={examCode}
              onChange={(e) => setExamCode(e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Candidate Email"
              value={candidateEmail}
              onChange={(e) => setCandidateEmail(e.target.value)}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={fetchResults}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <GetAppIcon />}
            >
              Get Results
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {questions.length > 0 && (
        <Paper style={{ padding: 24, marginBottom: 24 }}>
          <Typography variant="h6" gutterBottom>
            Questions ({questions.length})
          </Typography>
          {questions.map((question, index) => (
            <Accordion key={question.id} style={{ marginBottom: 8 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography>
                    {question.sectionTitle} - Question {question.questionIndex + 1}
                  </Typography>
                  <Chip
                    label={question.type || 'text'}
                    size="small"
                    style={{ marginRight: 16 }}
                  />
                  {question.evaluation && (
                    <Chip
                      label={`Score: ${question.evaluation.score || 0}/${question.evaluation.maxScore || question.maxScore || 10}`}
                      size="small"
                      color="primary"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box width="100%">
                  <Typography variant="subtitle2" gutterBottom>
                    Question:
                  </Typography>
                  <Typography variant="body2" style={{ marginBottom: 16 }}>
                    {question.text}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => handleQuestionSelect(question)}
                    startIcon={<PlayArrowIcon />}
                  >
                    View Details
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      )}

      {selectedQuestion && (
        <Paper style={{ padding: 24 }}>
          <Typography variant="h6" gutterBottom>
            Question Details
          </Typography>
          <Divider style={{ marginBottom: 24 }} />

          <Grid container spacing={3}>
            {/* Original Evaluation */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                Original Evaluation
              </Typography>
              
              <Box style={{ marginBottom: 16 }}>
                <Typography variant="caption" color="textSecondary">
                  Extracted Text Source: {selectedQuestion.extractionSource}
                </Typography>
                <Paper variant="outlined" style={{ padding: 12, marginTop: 8, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedQuestion.extractedText || 'No text extracted'}
                  </Typography>
                </Paper>
              </Box>

              {selectedQuestion.evaluation && (
                <>
                  <Box style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle2">Score:</Typography>
                    <Chip
                      label={`${selectedQuestion.evaluation.score || 0} / ${selectedQuestion.evaluation.maxScore || selectedQuestion.maxScore || 10}`}
                      color="primary"
                      style={{ marginTop: 4 }}
                    />
                  </Box>

                  <Box style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle2">Feedback:</Typography>
                    <Paper variant="outlined" style={{ padding: 12, marginTop: 8, maxHeight: 300, overflow: 'auto' }}>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {selectedQuestion.evaluation.feedback || 'No feedback available'}
                      </Typography>
                    </Paper>
                  </Box>

                  {selectedQuestion.evaluation.criteriaBreakdown && (
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2">Criteria Breakdown:</Typography>
                      <Paper variant="outlined" style={{ padding: 12, marginTop: 8 }}>
                        {Object.values(selectedQuestion.evaluation.criteriaBreakdown).map((criterion, idx) => (
                          <Box key={idx} style={{ marginBottom: 8 }}>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {criterion.criterion || criterion.evaluationKey || `Criterion ${idx + 1}`}
                            </Typography>
                            <Typography variant="caption">
                              {criterion.score !== undefined && criterion.maxScore !== undefined ? (
                                <>Score: {criterion.score}/{criterion.maxScore} | </>
                              ) : null}
                              {criterion.satisfactionScore !== undefined ? (
                                <>Satisfaction: {criterion.satisfactionScore}%</>
                              ) : null}
                            </Typography>
                            {criterion.feedback && (
                              <Typography variant="caption" style={{ display: 'block', marginTop: 4, fontStyle: 'italic' }}>
                                {criterion.feedback}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}
                </>
              )}

              <Box style={{ marginTop: 16 }}>
                <Typography variant="subtitle2">Prompt Used (Reconstructed):</Typography>
                <Paper variant="outlined" style={{ padding: 12, marginTop: 8, maxHeight: 200, overflow: 'auto' }}>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {buildPrompt(selectedQuestion, selectedQuestion.extractedText)}
                  </Typography>
                </Paper>
              </Box>
            </Grid>

            {/* Custom Evaluation */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold' }}>
                Custom Prompt Evaluation
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={6}
                label="Custom Prompt"
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                variant="outlined"
                placeholder="Enter your evaluation instructions. Question, answer, and evaluation criteria will be automatically added by the system."
                style={{ marginBottom: 16 }}
                helperText="Just enter your evaluation instructions. The system will automatically add the question, user's answer, and evaluation criteria."
              />

              <Button
                variant="contained"
                color="secondary"
                onClick={reEvaluateWithCustomPrompt}
                disabled={evaluating || !customPrompt.trim()}
                startIcon={evaluating ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                style={{ marginBottom: 16 }}
              >
                Re-evaluate with Custom Prompt
              </Button>

              {customEvaluation && (
                <>
                  <Paper 
                    style={{ 
                      marginBottom: 16, 
                      padding: 12, 
                      backgroundColor: '#e3f2fd',
                      borderLeft: '4px solid #2196f3'
                    }}
                  >
                    <Typography variant="body2" style={{ color: '#1976d2' }}>
                      ℹ️ This is a comparison only. Results are not saved to Firebase.
                    </Typography>
                  </Paper>

                  <Box style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle2">Score:</Typography>
                    <Chip
                      label={`${customEvaluation.score || 0} / ${customEvaluation.maxScore || selectedQuestion.maxScore || 10}`}
                      color="secondary"
                      style={{ marginTop: 4 }}
                    />
                  </Box>

                  <Box style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle2">Feedback:</Typography>
                    <Paper variant="outlined" style={{ padding: 12, marginTop: 8, maxHeight: 300, overflow: 'auto' }}>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {(() => {
                          const feedback = customEvaluation.feedback;
                          if (!feedback) return 'No feedback available';
                          // If feedback is an object, convert it to a readable string
                          if (typeof feedback === 'object') {
                            return JSON.stringify(feedback, null, 2);
                          }
                          return feedback;
                        })()}
                      </Typography>
                    </Paper>
                  </Box>

                  {customEvaluation.criteriaBreakdown && (
                    <Box style={{ marginBottom: 16 }}>
                      <Typography variant="subtitle2">Criteria Breakdown:</Typography>
                      <Paper variant="outlined" style={{ padding: 12, marginTop: 8 }}>
                        {Object.values(customEvaluation.criteriaBreakdown).map((criterion, idx) => (
                          <Box key={idx} style={{ marginBottom: 8 }}>
                            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                              {criterion.criterion || criterion.evaluationKey || `Criterion ${idx + 1}`}
                            </Typography>
                            <Typography variant="caption">
                              {criterion.score !== undefined && criterion.maxScore !== undefined ? (
                                <>Score: {criterion.score}/{criterion.maxScore} | </>
                              ) : null}
                              {criterion.satisfactionScore !== undefined ? (
                                <>Satisfaction: {criterion.satisfactionScore}%</>
                              ) : null}
                            </Typography>
                            {criterion.feedback && (
                              <Typography variant="caption" style={{ display: 'block', marginTop: 4, fontStyle: 'italic' }}>
                                {criterion.feedback}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Paper>
                    </Box>
                  )}

                  <Box style={{ marginTop: 16 }}>
                    <Typography variant="subtitle2">Prompt Used:</Typography>
                    <Paper variant="outlined" style={{ padding: 12, marginTop: 8, maxHeight: 200, overflow: 'auto' }}>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {customEvaluation.promptUsed}
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}
    </div>
  );
};

export default Playground;

